import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import {
  I18N,
  IMenuCategory,
  IMenuItem,
  PermissionService,
  TranslatePipe,
} from 'O3_core';
import { sidebarMenuItems } from '../constants/sidebar-menu-items';
import { HealthCheckService, IHealthCheck } from '../health-check.service';
import { MessagingOrchestratorService } from 'O3_communication';

@Component({
  selector: 'o3-core-app-sidebar',
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    TextBoxModule,
    TranslatePipe,
  ],
  templateUrl: './app-sidebar.html',
  styleUrl: './app-sidebar.scss',
})
export class AppSidebar implements OnInit {
  private readonly i18n = inject(I18N);
  private readonly healthCheckService = inject(HealthCheckService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messagingOrchestrator = inject(MessagingOrchestratorService);

  @Input() isSidebarCollapsed = false;

  permittedMenuItems = signal<IMenuCategory[]>([]);
  isLoadingMenu = signal<boolean>(true);
  searchTerm = signal<string>('');
  healthCheck = signal<IHealthCheck | null>(null);

  ngOnInit() {
    const currentUrl = location.pathname;

    this.permissionService
      .load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (perms) => {
          const filtered = sidebarMenuItems
            .map((category) => ({
              ...category,
              items: this.filterItemsByPermissions(category.items, perms),
            }))
            .filter((category) => category.items.length > 0);

          for (const category of filtered) {
            this.expandActiveItems(category.items, currentUrl);
          }

          this.permittedMenuItems.set(filtered);
          this.isLoadingMenu.set(false);
        },
      });

    this.healthCheckService
      .getHealthCheck()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (data) => this.healthCheck.set(data) });
  }

  private filterItemsByPermissions(
    items: IMenuItem[],
    perms: Set<string>,
  ): IMenuItem[] {
    const result: IMenuItem[] = [];
    for (const item of items) {
      if (item.items) {
        // Parent node: include only if it has at least one visible child
        const visibleChildren = this.filterItemsByPermissions(
          item.items,
          perms,
        );
        if (visibleChildren.length > 0) {
          result.push({ ...item, items: visibleChildren });
        }
      } else {
        // Leaf node: include if no permission required, or user has a matching permission
        if (
          !item.permission ||
          this.hasResourcePermission(item.permission, perms)
        ) {
          result.push(item);
        }
      }
    }
    return result;
  }

  private hasResourcePermission(permission: string, perms: Set<string>): boolean {
    // Exact match only: menu items carry dedicated UI visibility scopes
    // (`resource:SideMenuItem`). Prefix matching would let ANY grant on the
    // resource (`resource:Filter`, ...) satisfy a scoped gate, defeating the
    // dedicated scopes. Resource-only permission strings keep working because
    // the backend emits the bare resource name alongside `resource:scope`.
    return perms.has(permission);
  }

  private expandActiveItems(items: IMenuItem[], url: string): boolean {
    for (const item of items) {
      // open the item if the current url starts with the item's router link
      if (item.routerLink && url.startsWith(item.routerLink)) {
        item.isOpen = true;
        return true;
      }
      // now open the parent item if it has children
      if (item.items && this.expandActiveItems(item.items, url)) {
        item.isOpen = true;
        return true;
      }
    }
    return false;
  }

  /** Total unread message count for the sidebar badge */
  totalUnread = this.messagingOrchestrator.totalUnread;

  filteredMenuItems = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) {
      return this.permittedMenuItems();
    }
    return this.permittedMenuItems()
      .map((category) => ({
        ...category,
        items: this.filterMenuItems(category.items, term),
      }))
      .filter((category) => category.items.length > 0);
  });

  private filterMenuItems(items: IMenuItem[], searchTerm: string): IMenuItem[] {
    const filtered: IMenuItem[] = [];

    for (const item of items) {
      const translatedLabel = this.i18n.t(item.label).toLowerCase();
      const matches = translatedLabel.includes(searchTerm);

      // Check if any child items match
      const filteredChildren = item.items
        ? this.filterMenuItems(item.items, searchTerm)
        : [];

      // Include item if it matches or has matching children
      if (matches || filteredChildren.length > 0) {
        filtered.push({
          ...item,
          items: filteredChildren.length > 0 ? filteredChildren : item.items,
          isOpen: filteredChildren.length > 0 ? true : item.isOpen,
        });
      }
    }

    return filtered;
  }

  onSearchChange(value: string) {
    this.searchTerm.set(value);
  }

  clearSearch() {
    this.searchTerm.set('');
  }

  toggleMenuItem(item: IMenuItem) {
    if (!this.isSidebarCollapsed && item.items) {
      item.isOpen = !item.isOpen;
    }
  }
}
